import * as React from 'react';
import classnames from 'classnames';
import { CoreRadioButton as CoreRadio } from '../internal/WixUiCoreComponents/components/CoreRadioButton';
import { st, classes } from './Ratings.st.css';
import { Favorite, FavoriteFill } from '@wix/wix-ui-icons-common/on-stage';
import {
  FavoriteFillLarge,
  FavoriteLarge,
} from '@wix/wix-ui-icons-common/on-stage/system';
import { RATINGS_DATA_HOOKS, RATINGS_DATA_KEYS } from './dataHooks';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import {
  RatingsSize,
  RatingsMode,
  RatingsLayout,
  RatingsProps,
  RatingsDefaultProps,
  RatingsState,
  RatingsLabelPlacement,
  RatingsDataHooks,
} from './types';
import uniqueId from '../../common/uniqueId';

const STARS_INDEXES_ARR = [0, 1, 2, 3, 4];

const partialIconBuilder = (IconFill, iconSize) => (coveredPart: number) =>
  (
    <div
      className={classes.partialIconContainer}
      style={{ width: iconSize, height: iconSize }}
    >
      <IconFill className={classes.partialIconBackground} />
      <div
        className={classes.partialIconOverlay}
        style={{
          width: `${coveredPart * 100}%`,
        }}
      >
        <IconFill />
      </div>
    </div>
  );

const icons = {
  small: {
    empty: <Favorite />,
    full: <FavoriteFill />,
    partial: partialIconBuilder(FavoriteFill, 24),
  },
  large: {
    empty: <FavoriteLarge />,
    full: <FavoriteFillLarge />,
    partial: partialIconBuilder(FavoriteFillLarge, 32),
  },
};

const toNearestStep = (valueToRound, step = 0.5) =>
  parseFloat((Math.round(valueToRound / step) * step).toFixed(1));

/** Ratings component based on IconToggle */
export class Ratings extends React.Component<RatingsProps, RatingsState> {
  static displayName = 'Ratings';
  private readonly _errorMessageId: string;
  private readonly starsRefs = STARS_INDEXES_ARR.map(() =>
    React.createRef<CoreRadio>(),
  );
  static defaultProps: RatingsDefaultProps = {
    value: 0,
    disabled: false,
    error: false,
    mode: RatingsMode.Input,
    size: RatingsSize.Small,
    layout: RatingsLayout.Aside,
    inputOptions: [],
    starsAriaLabels: [],
    labelPlacement: RatingsLabelPlacement.Top,
  };

  state: RatingsState = {
    currentHovered: -1,
  };

  constructor(props) {
    super(props);

    this._errorMessageId = uniqueId('error-message');
  }

  focus = () => {
    const { value } = this.props;

    const indexToFocus = value === 0 ? 0 : value - 1;
    const refToFocus = this.starsRefs[indexToFocus];

    refToFocus?.current?.focus();
  };

  _labelId: string = uniqueId('rating-label-id');
  _ratingsName: string = uniqueId('ratings');

  _getDataAttributes() {
    const { currentHovered } = this.state;
    const { disabled, error, size } = this.props;

    return {
      [RATINGS_DATA_KEYS.Disabled]: disabled,
      [RATINGS_DATA_KEYS.Error]: error,
      [RATINGS_DATA_KEYS.Size]: size,
      [RATINGS_DATA_KEYS.Hovered]: currentHovered + 1,
    };
  }

  _handleHoverIcon = (idx: number) => {
    this.setState({ currentHovered: idx });
  };

  _onClick = (idx: number) => {
    const { mode, onSelect } = this.props;

    if (mode === RatingsMode.Input && onSelect) {
      onSelect(idx);
    }
  };

  _renderInputOptions = () => {
    const { inputOptions, value } = this.props;
    const { currentHovered } = this.state;

    return (
      <div className={classes.labelList}>
        {currentHovered > -1 ? (
          <span
            data-hook={RATINGS_DATA_HOOKS.InputOption}
            className={classes.inputOption}
          >
            {inputOptions[currentHovered]}
          </span>
        ) : (
          <span
            data-hook={RATINGS_DATA_HOOKS.InputOptionCurrent}
            className={classnames(
              classes.inputOption,
              classes.inputOptionCurrent,
            )}
          >
            {inputOptions[value - 1]}
          </span>
        )}
      </div>
    );
  };

  _renderRatingInfo = () => {
    const { ratingDisplay, countDisplay } = this.props;
    const info = [];

    if (ratingDisplay) {
      info.push(ratingDisplay);
    }

    if (countDisplay) {
      info.push(countDisplay);
    }

    return (
      <div
        data-hook={RATINGS_DATA_HOOKS.RatingInfo}
        className={classes.ratingInfo}
      >
        {info.join(' | ')}
      </div>
    );
  };

  _renderIcon = (idx) => {
    const { mode, value, size } = this.props;
    const { currentHovered } = this.state;
    const isDisplay = mode === 'display';
    const isHovered = currentHovered >= 0 && idx <= currentHovered;
    const isSelected = idx < value;
    let iconTheme = 'empty';
    if (isDisplay || isHovered || (isSelected && currentHovered < 0)) {
      iconTheme = 'full';
    }
    let icon = icons[size][iconTheme];
    if (isDisplay && Math.floor(value) === idx) {
      const coveredPart = toNearestStep(value % 1);
      icon = coveredPart ? icons[size].partial(coveredPart) : icon;
    }
    return icon;
  };

  _handleListHover = ({ target }) => {
    if (target.tagName === 'DIV' && target.classList.contains(classes.icon)) {
      const hoveredIndex = parseInt(target.dataset.index, 10);
      const currentHovered = hoveredIndex >= 0 ? hoveredIndex : -1;
      this.setState({ currentHovered });
    }
  };

  _handleListUnHover = () => {
    this.setState({ currentHovered: -1 });
  };

  _onFocus = (idx) => {
    this._handleHoverIcon(idx);
  };

  _onBlur = () => {
    this._handleHoverIcon(-1);
  };

  render() {
    const {
      value,
      disabled,
      error,
      errorMessage,
      size,
      mode,
      inputOptions,
      ratingDisplay,
      countDisplay,
      className,
      label,
      labelPlacement,
      layout,
      starsAriaLabels,
      errorAppearance,
      lang,
    } = this.props;

    const ratingList = Array.from(new Array(5));
    const showInputOptions = inputOptions.length && mode === RatingsMode.Input;
    const showRatingInfo =
      (ratingDisplay || countDisplay) && mode === RatingsMode.Display;
    const isErrorMessageProvided =
      error && !!errorMessage && mode === RatingsMode.Input;
    const isInputOptionSelected = showInputOptions && value > 0 && value <= 5;
    const stStates = {
      disabled,
      error,
      hasErrorMessage: !!errorMessage,
      size,
      mode,
      layout,
      showInputOptions: !!showInputOptions,
      inputOptionSelected: isInputOptionSelected,
      labelPlacement,
    };

    return (
      <ErrorMessageWrapper
        {...{
          lang,
          visible: isErrorMessageProvided,
          message: errorMessage,
          messageId: this._errorMessageId,
          errorAppearance,
          className: st(classes.root, stStates, className),
          'data-hook': this.props['data-hook'],
        }}
      >
        <div
          className={classes.container}
          {...this._getDataAttributes()}
          role="group"
          aria-labelledby={
            mode === RatingsMode.Input && label ? this._labelId : undefined
          }
          data-hook={RatingsDataHooks.RatingContainer}
        >
          {mode === RatingsMode.Input && label && (
            <div
              id={this._labelId}
              data-hook={RATINGS_DATA_HOOKS.RatingLabel}
              className={classes.label}
            >
              {label}
            </div>
          )}
          <div className={classes.innerWrapper}>
            <div
              className={`${classes.iconList} ${!value ? classes.empty : ''}`}
              tabIndex={mode === RatingsMode.Display ? -1 : undefined}
              onMouseEnter={this._handleListHover}
              onMouseLeave={this._handleListUnHover}
              aria-hidden={mode === RatingsMode.Display}
            >
              {ratingList.map((_el, idx: number) => {
                const checked = idx + 1 === Math.floor(value);
                const defaultAriaLabel = inputOptions.length
                  ? inputOptions[idx]
                  : idx.toString();

                return (
                  <CoreRadio
                    ref={this.starsRefs[idx]}
                    value={`${idx + 1}`}
                    aria-label={
                      (starsAriaLabels && starsAriaLabels[idx]) ||
                      defaultAriaLabel
                    }
                    uncheckedIcon={this._renderIcon(idx)}
                    checkedIcon={this._renderIcon(idx)}
                    checked={checked}
                    disabled={mode === RatingsMode.Display || disabled}
                    onChange={() => this._onClick(idx + 1)}
                    onFocus={() => this._onFocus(idx)}
                    onBlur={this._onBlur}
                    name={this.props.name || this._ratingsName}
                    onHover={() => this._handleHoverIcon(idx)}
                    data-hook={RATINGS_DATA_HOOKS.IconWrapper}
                    className={st(classes.icon, { checked })}
                    key={idx}
                    data-index={idx}
                  />
                );
              })}
            </div>
            {!!showInputOptions && this._renderInputOptions()}
            {!!showRatingInfo && this._renderRatingInfo()}
          </div>
        </div>
      </ErrorMessageWrapper>
    );
  }
}
